/**
 * Amasty Multiple Wishlist compatibility for ScandiPWA
 * @copyright Scandiweb, Inc. All rights reserved.
 */

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { showNotification } from 'Store/Notification/Notification.action';
import { fetchMutation } from 'Util/Request/Mutation';

import { AmastyMultipleWishlistContext } from '../../context/AmastyMultipleWishlist';
import AmCreateWishlistMutationQuery from '../../query/AmCreateWishlistMutation.query';
import { WishlistPopupType } from '../../type/MultipleWishlist.type';
import { copyWishlist, moveWishlist } from '../../util/MultipleWishlist';
import {
    WISHLIST_ADD, WISHLIST_COPY, WISHLIST_MOVE, WISHLIST_OPERATION_COPY,
} from '../AmastyWishlistItem/AmastyWishlistItem.config';
import { tabMap } from '../MultipleWishlist/MultipleWishlist.config';
import MultipleWishlistPopup from './MultipleWishlistPopup.component';

export const BreadcrumbsDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/Breadcrumbs/Breadcrumbs.dispatcher'
);

/** @namespace Scandiweb/AmastyMultipleWishlist/Component/MultipleWishlistPopup/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    wishlistPopup: state.PopupReducer?.popupPayload,
});

/** @namespace Scandiweb/AmastyMultipleWishlist/Component/MultipleWishlistPopup/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    showNotification: (message, type) => {
        const messageType = type || 'success';

        return dispatch(showNotification(messageType, message));
    },
});

/** @namespace Scandiweb/AmastyMultipleWishlist/Component/MultipleWishlistPopup/Container */
export class MultipleWishlistPopupContainer extends PureComponent {
    static propTypes = {
        wishlistPopup: WishlistPopupType,
        showNotification: PropTypes.func,
    };

    static defaultProps = {
        wishlistPopup: {},
        showNotification: null,
    };

    static contextType = AmastyMultipleWishlistContext;

    operationMap = {
        [WISHLIST_COPY]: 'copy',
        [WISHLIST_MOVE]: 'move',
        [WISHLIST_ADD]: 'copy',
    };

    containerFunctions = {
        handleClick: this.handleClick.bind(this),
        handleChangeTab: this.handleChangeTab.bind(this),
        handleCreateListClick: this.handleCreateListClick.bind(this),
        handleCreateList: this.handleCreateList.bind(this),
        handleFieldChange: this.handleFieldChange.bind(this),
    };

    __construct(props) {
        super.__construct(props);

        this.state = {
            wishlistItem: {},
            activeTab: 0,
            isLoading: false,
            isCreateListClicked: false,
            createdList: '',
            isSaved: false,
            listName: '',
        };

        this.getWishlists();
    }

    handleCreateListClick() {
        const { isCreateListClicked } = this.state;

        this.setState({ isCreateListClicked: !isCreateListClicked });
    }

    async handleCreateList() {
        const { listName, activeTab } = this.state;
        const { showNotification } = this.props;

        const {
            createWishList: { message },
        } = await fetchMutation(AmCreateWishlistMutationQuery.getQuery(listName, tabMap[activeTab]));

        if (message === 'This name is already in use') {
            showNotification(__('This name is already in use'), 'error');
        } else {
            showNotification(__('Successfully created list'));
        }

        this.setState({
            listName: '',
            createdList: listName,
            isSaved: true,
        });

        this.getWishlists();
    }

    handleFieldChange(event) {
        const { currentTarget: { value } } = event;

        this.setState({ listName: value });
    }

    getWishlists() {
        const { fetchWishlists } = this.context;
        const { activeTab } = this.state;

        fetchWishlists(tabMap[activeTab]);
    }

    handleChangeTab(event) {
        const { fetchWishlists } = this.context;
        const { currentTarget: { value } } = event;

        this.setState({ activeTab: value, isLoading: true });

        fetchWishlists(tabMap[value]);

        this.setState({ isLoading: false });
    }

    handleClick(item) {
        const { wishlistPopup } = this.props;
        const { wishlistItem } = this.context;

        const operation = this.operationMap[Object.keys(wishlistPopup)[0]];

        if (operation === WISHLIST_OPERATION_COPY) {
            copyWishlist(item, wishlistItem);

            return;
        }

        moveWishlist(item, wishlistItem);
    }

    containerProps() {
        const {
            wishlistItem,
            activeTab,
            isLoading,
            isCreateListClicked,
            createdList,
            isSaved,
        } = this.state;
        const { showNotification } = this.props;
        const { wishlists } = this.context;

        return {
            wishlists,
            wishlistItem,
            activeTab,
            isLoading,
            isCreateListClicked,
            createdList,
            isSaved,
            showNotification,
        };
    }

    render() {
        return (
            <MultipleWishlistPopup
              { ...this.containerFunctions }
              { ...this.containerProps() }
            />
        );
    }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MultipleWishlistPopupContainer));
